import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Plus, X } from 'lucide-react';
import { useState } from 'react';
import type { DomainRequestListItem, OrganizationResponse, PlatformOrganizationListItem } from '@bm/contracts';
import { CUSTOM_DOMAIN_STATUS_LABELS } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { useLockBodyScroll } from '../lib/use-lock-body-scroll';
import { Alert, Button, Card, Field, Input } from '../components/ui';
import { PlatformTabs } from '../components/PlatformTabs';

/** Opened by "Neuen Verein anlegen" — creates the organisation and, in the
 * same step, its first Vereinsadministrator (POST .../admins right after),
 * so a freshly created club is never left without anyone able to manage it. */
function CreateOrgModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const org = await api<OrganizationResponse>('/platform/organizations', {
        method: 'POST',
        body: { name, shortName: shortName || undefined },
      });
      await api(`/platform/organizations/${org.id}/admins`, {
        method: 'POST',
        body: { email, password: password || undefined, firstName, lastName },
      });
      return org;
    },
    onSuccess: () => {
      onCreated();
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Verein konnte nicht angelegt werden.'),
  });
  useLockBodyScroll();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold text-slate-800">Neuen Verein anlegen</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X size={18} />
          </button>
        </div>

        {error && (
          <div className="mb-3">
            <Alert>{error}</Alert>
          </div>
        )}

        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            create.mutate();
          }}
        >
          <Field label="Vereinsname">
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Field label="Kurzname (optional)">
            <Input value={shortName} onChange={(e) => setShortName(e.target.value)} />
          </Field>

          <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Vereinsadministrator
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Vorname">
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            </Field>
            <Field label="Nachname">
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} required />
            </Field>
          </div>
          <Field label="E-Mail">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </Field>
          <Field label="Passwort (optional)">
            <Input
              type="text"
              placeholder="mind. 10 Zeichen"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <p className="text-xs text-slate-400">
            Legt automatisch auch einen Mitgliedsdatensatz in diesem Verein an — ein Vereinsadmin
            steht damit immer selbst in der Mitgliederliste. Ohne Passwort wird eine Einladung per
            E-Mail verschickt.
          </p>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || !firstName.trim() || !lastName.trim() || !email.trim() || create.isPending}
            >
              {create.isPending ? 'Wird angelegt …' : 'Verein anlegen'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** "Domain-Freigaben" — Schritt 2 der "Eigene Domain"-Einrichtung
 * (Verwaltung › Verein). Freigeben lässt den Verein selbst per Selbst-Check
 * aktivieren (`POST /org/custom-domain/verify`); "Als aktiv markieren" ist
 * der manuelle Fallback für Setups, in denen der Server sich selbst über die
 * öffentliche Domain nicht erreichen kann (Hairpin-NAT). */
function DomainRequestsCard() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['platform', 'domain-requests'],
    queryFn: () => api<DomainRequestListItem[]>('/platform/organizations/domain-requests'),
  });
  const [rejectReason, setRejectReason] = useState<Record<string, string>>({});
  const [showRejectFor, setShowRejectFor] = useState<string | null>(null);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['platform', 'domain-requests'] });

  const approve = useMutation({
    mutationFn: (id: string) => api(`/platform/organizations/${id}/domain/approve`, { method: 'POST' }),
    onSuccess: invalidate,
  });
  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api(`/platform/organizations/${id}/domain/reject`, { method: 'POST', body: { reason: reason || undefined } }),
    onSuccess: (_r, { id }) => {
      setShowRejectFor(null);
      setRejectReason((m) => ({ ...m, [id]: '' }));
      invalidate();
    },
  });
  const activate = useMutation({
    mutationFn: (id: string) => api(`/platform/organizations/${id}/domain/activate`, { method: 'POST' }),
    onSuccess: invalidate,
  });

  if (data && data.length === 0) return null;

  return (
    <Card title="Domain-Freigaben">
      <p className="mb-3 text-xs text-slate-400">
        Vereine, die eine eigene Domain eingetragen und Freigabe angefragt haben. Nach "Freigeben" muss die Domain
        noch manuell bei der App in Coolify eingetragen werden — der Verein kann den Status danach selbst per
        Selbst-Check auf "Aktiv" bringen, oder du bestätigst es hier direkt.
      </p>
      {/* Desktop: Tabelle. Mobile (`md:hidden` unten): Zeilen-Liste — gleiches
       * Muster wie PlatformUsersPage/OrgTrainersPage (§ Nutzeranfrage
       * „restliche Seiten … in der mobilen Ansicht anpassen"), hier mit der
       * Ablehnen-Begründung und den Aktions-Buttons untereinander statt in
       * einer immer enger werdenden letzten Spalte. */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead className="text-left text-slate-500">
            <tr>
              <th className="py-1.5 pr-3">Verein</th>
              <th className="py-1.5 pr-3">Domain</th>
              <th className="py-1.5 pr-3">Status</th>
              <th className="py-1.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data?.map((r) => (
              <tr key={r.organizationId}>
                <td className="py-1.5 pr-3 font-medium">{r.organizationName}</td>
                <td className="py-1.5 pr-3 text-slate-500">
                  <code>{r.domain}</code>
                </td>
                <td className="py-1.5 pr-3 text-slate-500">
                  {CUSTOM_DOMAIN_STATUS_LABELS[r.status]}
                  {r.status === 'rejected' && r.rejectionReason && (
                    <span className="block text-xs text-slate-400">{r.rejectionReason}</span>
                  )}
                </td>
                <td className="whitespace-nowrap py-1.5 text-right">
                  {r.status === 'pending_approval' && (
                    <>
                      <Button
                        className="gap-1 px-2 py-1 text-xs"
                        disabled={approve.isPending}
                        onClick={() => approve.mutate(r.organizationId)}
                      >
                        <Check size={13} /> Freigeben
                      </Button>{' '}
                      <Button
                        variant="ghost"
                        className="px-2 py-1 text-xs"
                        onClick={() => setShowRejectFor(showRejectFor === r.organizationId ? null : r.organizationId)}
                      >
                        Ablehnen
                      </Button>
                      {showRejectFor === r.organizationId && (
                        <div className="mt-1.5 flex items-center justify-end gap-1.5">
                          <input
                            className="w-40 rounded-md border border-slate-200 px-2 py-1 text-xs focus:border-brand-accent focus:outline-none"
                            placeholder="Grund (optional)"
                            value={rejectReason[r.organizationId] ?? ''}
                            onChange={(e) => setRejectReason((m) => ({ ...m, [r.organizationId]: e.target.value }))}
                          />
                          <Button
                            variant="danger"
                            className="px-2 py-1 text-xs"
                            disabled={reject.isPending}
                            onClick={() => reject.mutate({ id: r.organizationId, reason: rejectReason[r.organizationId] ?? '' })}
                          >
                            Bestätigen
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                  {r.status === 'approved' && (
                    <Button
                      variant="ghost"
                      className="px-2 py-1 text-xs"
                      disabled={activate.isPending}
                      onClick={() => activate.mutate(r.organizationId)}
                    >
                      Als aktiv markieren
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="divide-y divide-slate-100 md:hidden">
        {data?.map((r) => (
          <div key={r.organizationId} className="py-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <span className="min-w-0 truncate text-sm font-semibold text-slate-800">{r.organizationName}</span>
              <span className="shrink-0 text-xs text-slate-500">{CUSTOM_DOMAIN_STATUS_LABELS[r.status]}</span>
            </div>
            <code className="mt-0.5 block truncate text-xs text-slate-500">{r.domain}</code>
            {r.status === 'rejected' && r.rejectionReason && (
              <p className="mt-0.5 text-xs text-slate-400">{r.rejectionReason}</p>
            )}
            {r.status === 'pending_approval' && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button
                  className="gap-1 px-2 py-1 text-xs"
                  disabled={approve.isPending}
                  onClick={() => approve.mutate(r.organizationId)}
                >
                  <Check size={13} /> Freigeben
                </Button>
                <Button
                  variant="ghost"
                  className="px-2 py-1 text-xs"
                  onClick={() => setShowRejectFor(showRejectFor === r.organizationId ? null : r.organizationId)}
                >
                  Ablehnen
                </Button>
              </div>
            )}
            {showRejectFor === r.organizationId && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <input
                  className="min-w-0 flex-1 rounded-md border border-slate-200 px-2 py-1 text-xs focus:border-brand-accent focus:outline-none"
                  placeholder="Grund (optional)"
                  value={rejectReason[r.organizationId] ?? ''}
                  onChange={(e) => setRejectReason((m) => ({ ...m, [r.organizationId]: e.target.value }))}
                />
                <Button
                  variant="danger"
                  className="shrink-0 px-2 py-1 text-xs"
                  disabled={reject.isPending}
                  onClick={() => reject.mutate({ id: r.organizationId, reason: rejectReason[r.organizationId] ?? '' })}
                >
                  Bestätigen
                </Button>
              </div>
            )}
            {r.status === 'approved' && (
              <div className="mt-2">
                <Button
                  variant="ghost"
                  className="px-2 py-1 text-xs"
                  disabled={activate.isPending}
                  onClick={() => activate.mutate(r.organizationId)}
                >
                  Als aktiv markieren
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

export function PlatformOrgsPage() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['platform', 'orgs'],
    queryFn: () => api<PlatformOrganizationListItem[]>('/platform/organizations'),
  });

  const [showCreate, setShowCreate] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [editName, setEditName] = useState<Record<string, string>>({});
  const [editShortName, setEditShortName] = useState<Record<string, string>>({});
  const [editError, setEditError] = useState<Record<string, string>>({});

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['platform', 'orgs'] });
    void qc.invalidateQueries({ queryKey: ['platform', 'admins'] });
    void qc.invalidateQueries({ queryKey: ['me'] });
  };

  const updateOrg = useMutation({
    mutationFn: ({ id, name: n, shortName: sn }: { id: string; name: string; shortName: string }) =>
      api(`/platform/organizations/${id}`, { method: 'PATCH', body: { name: n, shortName: sn || undefined } }),
    onSuccess: (_r, { id }) => {
      setEditName((m) => {
        const { [id]: _drop, ...rest } = m;
        return rest;
      });
      setEditShortName((m) => {
        const { [id]: _drop, ...rest } = m;
        return rest;
      });
      setEditError((m) => ({ ...m, [id]: '' }));
      void qc.invalidateQueries({ queryKey: ['platform', 'orgs'] });
    },
    onError: (e, { id }) => setEditError((m) => ({ ...m, [id]: e instanceof ApiError ? e.message : 'Fehlgeschlagen.' })),
  });

  const deleteOrg = useMutation({
    mutationFn: (id: string) => api(`/platform/organizations/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['platform', 'orgs'] }),
  });

  const syncRoles = useMutation({
    mutationFn: () => api<{ orgsTouched: number }>('/platform/organizations/sync-roles', { method: 'POST' }),
    onSuccess: (r) => {
      setSyncResult(
        r.orgsTouched > 0
          ? `${r.orgsTouched} Vereinsrolle(n) aktualisiert.`
          : 'Bereits aktuell — keine Änderungen nötig.',
      );
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (e) => setSyncResult(e instanceof ApiError ? e.message : 'Fehlgeschlagen.'),
  });

  return (
    <div className="space-y-4">
      <PlatformTabs />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="hidden text-xl font-semibold md:block">Vereine (Plattform)</h1>
        <div className="flex flex-wrap items-center gap-2">
          {syncResult && <span className="text-xs text-slate-500">{syncResult}</span>}
          <Button
            variant="ghost"
            disabled={syncRoles.isPending}
            onClick={() => {
              setSyncResult(null);
              syncRoles.mutate();
            }}
          >
            Rollen aktualisieren
          </Button>
        </div>
      </div>
      <p className="-mt-2 text-xs text-slate-400">
        Gleicht die System-Rollen aller Vereine additiv mit dem aktuellen Berechtigungskatalog ab —
        holt neu hinzugekommene Berechtigungen für Vereine nach, die vor der jeweiligen Funktion
        angelegt wurden. Läuft normalerweise automatisch bei jedem Deploy.
      </p>

      <DomainRequestsCard />

      <Card>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-slate-800">{data?.length ?? 0} Vereine</h2>
          <Button className="gap-1.5" onClick={() => setShowCreate(true)}>
            <Plus size={16} /> Neuen Verein anlegen
          </Button>
        </div>
        {/* Desktop: Tabelle. Mobile (`md:hidden` unten): Zeilen-Liste mit
         * echten `Field`/`Input`-Paaren statt der dichten Tabellen-Inputs —
         * sechs Spalten inkl. zweier Live-Eingabefelder und zwei
         * Aktions-Buttons liefen auf Handy-Breite ohne jeden Scroll-Hinweis
         * über den Rand hinaus (§ Nutzeranfrage „restliche Seiten … in der
         * mobilen Ansicht anpassen", gleiches Muster wie PlatformUsersPage/
         * OrgTrainersPage). */}
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500">
              <tr>
                <th className="py-1.5 pr-3">Name</th>
                <th className="py-1.5 pr-3">Kurzname</th>
                <th className="py-1.5 pr-3">Status</th>
                <th className="py-1.5 pr-3">Mitglieder</th>
                <th className="py-1.5 pr-3">Speicher</th>
                <th className="py-1.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data?.map((org) => {
                const nameValue = editName[org.id] ?? org.name;
                const shortNameValue = editShortName[org.id] ?? org.shortName ?? '';
                const changed = nameValue !== org.name || shortNameValue !== (org.shortName ?? '');
                return (
                  <tr key={org.id}>
                    <td className="py-1.5 pr-3">
                      <input
                        className="w-40 rounded-md border border-slate-200 px-2 py-1 text-sm focus:border-brand-accent focus:outline-none"
                        value={nameValue}
                        onChange={(e) => setEditName((m) => ({ ...m, [org.id]: e.target.value }))}
                      />
                    </td>
                    <td className="py-1.5 pr-3">
                      <input
                        className="w-24 rounded-md border border-slate-200 px-2 py-1 text-sm focus:border-brand-accent focus:outline-none"
                        value={shortNameValue}
                        onChange={(e) => setEditShortName((m) => ({ ...m, [org.id]: e.target.value }))}
                      />
                    </td>
                    <td className="py-1.5 pr-3 text-slate-500">{org.status}</td>
                    <td className="py-1.5 pr-3 text-slate-500">{org.memberCount}</td>
                    <td className="py-1.5 pr-3 whitespace-nowrap text-slate-500">
                      {(org.storageBytes / 1024).toFixed(0)} KB
                    </td>
                    <td className="whitespace-nowrap py-1.5 text-right">
                      {changed && (
                        <Button
                          variant="ghost"
                          className="px-2 py-1 text-xs"
                          disabled={!nameValue.trim() || updateOrg.isPending}
                          onClick={() =>
                            updateOrg.mutate({ id: org.id, name: nameValue.trim(), shortName: shortNameValue })
                          }
                        >
                          Speichern
                        </Button>
                      )}
                      <Button
                        variant="danger"
                        className="px-2 py-1 text-xs"
                        disabled={deleteOrg.isPending}
                        onClick={() => {
                          if (
                            confirm(
                              `„${org.name}" komplett löschen? Alle Mitglieder, Mannschaften, Trainings- und Saisondaten dieses Vereins werden unwiderruflich entfernt.`,
                            )
                          ) {
                            deleteOrg.mutate(org.id);
                          }
                        }}
                      >
                        Löschen
                      </Button>
                      {editError[org.id] && (
                        <div className="mt-1">
                          <Alert>{editError[org.id]}</Alert>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {data?.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-3 text-slate-500">
                    Noch keine Vereine angelegt.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="divide-y divide-slate-100 md:hidden">
          {data?.map((org) => {
            const nameValue = editName[org.id] ?? org.name;
            const shortNameValue = editShortName[org.id] ?? org.shortName ?? '';
            const changed = nameValue !== org.name || shortNameValue !== (org.shortName ?? '');
            return (
              <div key={org.id} className="py-3">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Name">
                    <Input
                      value={nameValue}
                      onChange={(e) => setEditName((m) => ({ ...m, [org.id]: e.target.value }))}
                      className="px-2.5 py-1.5"
                    />
                  </Field>
                  <Field label="Kurzname">
                    <Input
                      value={shortNameValue}
                      onChange={(e) => setEditShortName((m) => ({ ...m, [org.id]: e.target.value }))}
                      className="px-2.5 py-1.5"
                    />
                  </Field>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                  <span>{org.status}</span>
                  <span>{org.memberCount} Mitglieder</span>
                  <span>{(org.storageBytes / 1024).toFixed(0)} KB</span>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  {changed && (
                    <Button
                      variant="ghost"
                      className="px-2 py-1 text-xs"
                      disabled={!nameValue.trim() || updateOrg.isPending}
                      onClick={() => updateOrg.mutate({ id: org.id, name: nameValue.trim(), shortName: shortNameValue })}
                    >
                      Speichern
                    </Button>
                  )}
                  <Button
                    variant="danger"
                    className="px-2 py-1 text-xs"
                    disabled={deleteOrg.isPending}
                    onClick={() => {
                      if (
                        confirm(
                          `„${org.name}" komplett löschen? Alle Mitglieder, Mannschaften, Trainings- und Saisondaten dieses Vereins werden unwiderruflich entfernt.`,
                        )
                      ) {
                        deleteOrg.mutate(org.id);
                      }
                    }}
                  >
                    Löschen
                  </Button>
                </div>
                {editError[org.id] && (
                  <div className="mt-1.5">
                    <Alert>{editError[org.id]}</Alert>
                  </div>
                )}
              </div>
            );
          })}
          {data?.length === 0 && <p className="py-3 text-sm text-slate-500">Noch keine Vereine angelegt.</p>}
        </div>
      </Card>

      {showCreate && <CreateOrgModal onClose={() => setShowCreate(false)} onCreated={invalidate} />}
    </div>
  );
}
